/**
 * Where the camera was left in each place (spec/formats.md, Views; Daniel, 2026-10-25: each place
 * has its own camera, where it was last left, every clearing too): the run last shown in each mode
 * and each clearing by its id, so that each place comes back as the camera left it, after a reload
 * too. Kept in this browser, as a camera belongs to its screen.
 */
import type { Mode } from "@mistsweeper/core";
import type { CameraView } from "@mistsweeper/renderer";
import { getJson, setJson } from "./storage.ts";

const KEY = "views";

/** A run's view, with the name of the run it belongs to. */
export interface RunView extends CameraView {
  readonly run: string;
}

export interface Views {
  readonly v: 1;
  /** By mode, the view of the run last shown in it. */
  readonly runs: Readonly<Partial<Record<Mode, RunView>>>;
  /** By a clearing's id in decimal, the view last shown in it. */
  readonly clearings: Readonly<Record<string, CameraView>>;
}

export const NO_VIEWS: Views = { v: 1, runs: {}, clearings: {} };

/** The name a run's view is kept under: its seed and its start, joined by a colon. */
export function runName(game: { readonly seed: number; readonly startedAt: number }): string {
  return `${game.seed}:${game.startedAt}`;
}

function finite(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** The five numbers of a view, and nothing else it carries. */
function plain(view: CameraView): CameraView {
  return { x: view.x, y: view.y, yaw: view.yaw, pitch: view.pitch, scale: view.scale };
}

/** A view, or null when one of its numbers is missing or not finite. */
function parseView(raw: unknown): CameraView | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { x, y, yaw, pitch, scale } = raw as Record<string, unknown>;
  if (!finite(x) || !finite(y) || !finite(yaw) || !finite(pitch) || !finite(scale)) return null;
  return { x, y, yaw, pitch, scale };
}

/**
 * The views of a stored document: none for a document of another version or none at all, and
 * without each view that does not read, or a run's view that names no run.
 */
export function parseViews(raw: unknown): Views {
  if (typeof raw !== "object" || raw === null || (raw as { v?: unknown }).v !== 1) return NO_VIEWS;
  const { runs: rawRuns, clearings: rawClearings } = raw as Record<string, unknown>;
  const runs: Partial<Record<Mode, RunView>> = {};
  if (typeof rawRuns === "object" && rawRuns !== null) {
    for (const mode of ["daily", "endless"] as const) {
      const entry = (rawRuns as Record<string, unknown>)[mode];
      const view = parseView(entry);
      const run = view && (entry as Record<string, unknown>).run;
      if (view && typeof run === "string") runs[mode] = { run, ...view };
    }
  }
  const clearings: Record<string, CameraView> = {};
  if (typeof rawClearings === "object" && rawClearings !== null) {
    for (const [id, entry] of Object.entries(rawClearings)) {
      const view = parseView(entry);
      if (view && /^[1-9][0-9]*$/.test(id)) clearings[id] = view;
    }
  }
  return { v: 1, runs, clearings };
}

export function loadViews(): Views {
  return parseViews(getJson(KEY));
}

export function saveViews(views: Views): void {
  setJson(KEY, views);
}

/** The views with a run's view under its mode, in place of the one there. */
export function withRunView(views: Views, mode: Mode, run: string, view: CameraView): Views {
  return { ...views, runs: { ...views.runs, [mode]: { run, ...plain(view) } } };
}

/** The views with a clearing's view, in place of the one there, or without it for null. */
export function withClearingView(views: Views, id: number, view: CameraView | null): Views {
  const key = String(id);
  const clearings = Object.fromEntries(Object.entries(views.clearings).filter(([k]) => k !== key));
  if (view) clearings[key] = plain(view);
  return { ...views, clearings };
}
